import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  OAuthProvider
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, useMockData } from './config';

const getAzureTenantId = (): string | undefined => {
  return import.meta.env.VITE_AZURE_TENANT_ID;
};
import { User, Role } from '../../types';

export const signInWithEmail = async (email: string, password: string): Promise<User | null> => {
  if (useMockData || !auth) {
    throw new Error('Firebase not configured. Using mock mode.');
  }

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db!, 'users', userCredential.user.uid));
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    
    // If user doc doesn't exist, create a basic one
    const newUser: any = {
      name: userCredential.user.displayName || email.split('@')[0],
      email: email,
      role: Role.BRAND,
      avatar: '',
    };
    
    await setDoc(doc(db!, 'users', userCredential.user.uid), newUser);
    return { id: userCredential.user.uid, ...newUser };
  } catch (error: any) {
    throw new Error(error.message || 'Failed to sign in');
  }
};

export const signUpWithEmail = async (
  email: string, 
  password: string, 
  userData: { name: string; role?: Role; company?: string; department?: string }
): Promise<User> => {
  if (useMockData || !auth) {
    throw new Error('Firebase not configured. Using mock mode.');
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update Firebase Auth profile
    if (userData.name) {
      await updateProfile(userCredential.user, { displayName: userData.name });
    }
    
    // Create user document in Firestore (only include defined fields)
    const newUser: any = {
      name: userData.name,
      email: email,
      role: userData.role || Role.BRAND,
      avatar: '',
    };
    
    // Only add optional fields if they're provided
    if (userData.company) {
      newUser.company = userData.company;
    }
    if (userData.department) {
      newUser.department = userData.department;
    }
    
    await setDoc(doc(db!, 'users', userCredential.user.uid), newUser);
    return { id: userCredential.user.uid, ...newUser };
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create account');
  }
};

export const signOut = async (): Promise<void> => {
  if (useMockData || !auth) {
    return;
  }
  
  try {
    await firebaseSignOut(auth);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to sign out');
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  if (useMockData || !auth || !auth.currentUser) {
    return null;
  }

  try {
    const userDoc = await getDoc(doc(db!, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
};

export const signInWithMicrosoft = async (): Promise<User | null> => {
  if (useMockData || !auth || !db) {
    throw new Error('Firebase not configured. Using mock mode.');
  }

  try {
    const provider = new OAuthProvider('microsoft.com');
    
    // Configure custom parameters
    const customParams: { tenant?: string; prompt: string } = {
      prompt: 'select_account' // Force account picker to appear
    };
    
    // Optionally restrict to specific Azure AD tenant
    const tenantId = getAzureTenantId();
    if (tenantId) {
      customParams.tenant = tenantId;
    }
    
    provider.setCustomParameters(customParams);
    
    console.log('[AUTH] Attempting popup sign-in (authDomain:', auth.config.authDomain, ')');
    const result = await signInWithPopup(auth, provider);

    const userDoc = await getDoc(doc(db, 'users', result.user.uid));

    if (userDoc.exists()) {
      console.log('[AUTH] Popup sign-in successful (existing user)');
      return { id: userDoc.id, ...userDoc.data() } as User;
    }

    const newUser: any = {
      name: result.user.displayName || result.user.email?.split('@')[0] || 'User',
      email: result.user.email || '',
      emailVerified: result.user.emailVerified,
      role: Role.BRAND,
      avatar: result.user.photoURL || '',
    };

    if (result.user.email) {
      const domain = result.user.email.split('@')[1];
      if (domain && domain !== 'outlook.com' && domain !== 'hotmail.com' && domain !== 'live.com') {
        newUser.company = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
      }
    }

    await setDoc(doc(db, 'users', result.user.uid), newUser);
    console.log('[AUTH] Popup sign-in successful (new user created)');
    return { id: result.user.uid, ...newUser };
  } catch (error: any) {
    console.error('Microsoft auth error:', error);
    throw new Error(error.message || 'Microsoft authentication failed');
  }
};

let redirectResultProcessed = false; // Global flag to prevent double-processing
let redirectResultPromise: Promise<User | null> | null = null; // Store the promise to prevent duplicate calls

export const handleMicrosoftRedirect = async (): Promise<User | null> => {
  if (useMockData || !auth || !db) {
    return null;
  }

  // If already processing, return the existing promise to prevent duplicate calls
  if (redirectResultPromise) {
    return redirectResultPromise;
  }

  // Prevent double-processing in React StrictMode
  if (redirectResultProcessed) {
    return null;
  }
  
  // Set the promise immediately to prevent race conditions
  redirectResultPromise = (async () => {

  try {
    const result = await getRedirectResult(auth);
    
    if (!result) {
      // No redirect result, user didn't come from Microsoft login
      // Check if auth.currentUser exists even though getRedirectResult was null
      // This can happen if the user was already authenticated
      if (auth.currentUser) {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = { id: userDoc.id, ...userDoc.data() } as User;
          return userData;
        }
      }
      
      return null;
    }

    redirectResultProcessed = true; // Mark as processed
    
    const userDoc = await getDoc(doc(db, 'users', result.user.uid));
    
    if (userDoc.exists()) {
      const userData = { id: userDoc.id, ...userDoc.data() } as User;
      return userData;
    }
    
    // Create user profile if doesn't exist
    const newUser: any = {
      name: result.user.displayName || result.user.email?.split('@')[0] || 'User',
      email: result.user.email || '',
      emailVerified: result.user.emailVerified,
      role: Role.BRAND,
      avatar: result.user.photoURL || '',
    };
    
    // Extract company from email domain if available
    if (result.user.email) {
      const domain = result.user.email.split('@')[1];
      if (domain && domain !== 'outlook.com' && domain !== 'hotmail.com' && domain !== 'live.com') {
        // Use domain as company name (you can customize this logic)
        newUser.company = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
      }
    }
    
    await setDoc(doc(db, 'users', result.user.uid), newUser);
    return { id: result.user.uid, ...newUser };
  } catch (error: any) {
    console.error('Microsoft redirect error:', error);
    throw new Error(error.message || 'Microsoft authentication failed');
  }
  })(); // Close the async IIFE and call it
  
  // Clear the promise after completion
  redirectResultPromise.finally(() => {
    redirectResultPromise = null;
  });
  
  return redirectResultPromise;
};

export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) => {
  if (useMockData || !auth) {
    // Return a no-op unsubscribe function for mock mode
    return () => {};
  }

  return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      const user = await getCurrentUser();
      callback(user);
    } else {
      callback(null);
    }
  });
};
